/* eslint-disable arrow-body-style */
import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { StyledDateInput } from "@src/common/styled-components";
import { LegalSexOptions, MEDIA_QUERIES, RelationshipOptions } from "@src/constants";
import {
  type FormDetails,
  useApplicationContext,
  useCanvasContext,
  useFormContext,
  useUTMContext,
} from "@src/context";
import { useCanvasPortalScrollFix } from "@src/hooks/useCanvasPortalScrollFix";
import { useIdleFormFlush } from "@src/hooks/useIdleFormFlush";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import { useCanvasPrefillSync } from "@src/services/canvas/prefill/useCanvasPrefillSync";
import ClientLogsService from "@src/services/client-logs.service";
import { PatientDataService, validatePatientDataPayload } from "@src/services/patient-data.service";
import {
  getEighteenYearsAgeDate,
  isValidDateOfBirth,
  validateDateOfBirth,
} from "@src/utils/date-utils";
import { validateName } from "@src/utils/name-validation";
import { mapContextToPatientData } from "@src/utils/patient-data-mapping";
import { buildSubmittedDataSnapshot } from "@src/utils/submitted-data-snapshot";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { dayjs } from "@uhg-abyss/web/tools/dayjs";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Card } from "@uhg-abyss/web/ui/Card";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import { Fragment, type SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { patientInfoFormIds } from "../../patientInformationNavigation";

export const PolicyHolderDetailsForm = () => {
  const { data, setData } = useApplicationContext(); // Access Application context
  const { utmTags } = useUTMContext(); // Access UTM context
  const { setHandleSubmit } = useFormContext(); // Access Form context
  const { isInSalesforce: canvasIsInSalesforce } = useCanvasContext();
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const [isLoading, setIsLoading] = useState(false);

  useCanvasPortalScrollFix(canvasIsInSalesforce);

  // All field labels sourced from src/constants/page-labels.ts
  const labels = useResolvedPageLabels("policyHolderForm");

  const FormSpacing = useMemo(
    () =>
      styled("div", {
        display: "flex",
        flexDirection: "column",
        gap: "$core.spacing.300",
      }),
    [],
  );
  const handleSubmit = async (): Promise<FormDetails> => {
    setIsLoading(true);
    if (!validateForm()) {
      ClientLogsService.logUserError(
        data.userUuid || "unknown",
        "PolicyHolderDetailsForm.handleSubmit",
        "Form has validation errors, unable to proceed",
      );
      setIsLoading(false);
      return { isCompleted: false };
    }

    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "PolicyHolderDetailsForm.handleSubmit",
      "Form validation passed - proceeding to map and save patient data",
      undefined,
      {},
    );

    // Save data to context first
    setData("policyholderFirstName", value.policyholderFirstName);
    setData("policyholderLastName", value.policyholderLastName);
    setData("relationshipToPolicyholder", value.relationshipToPolicyholder);
    setData("policyholderGender", value.policyholderGender);
    setData("policyholderDOB", value.policyholderDOB);

    // Map context data with current form values to avoid race condition
    const snapshotBase = buildSubmittedDataSnapshot(data);

    const currentContextData = {
      ...snapshotBase,
      policyholderFirstName: value.policyholderFirstName,
      policyholderLastName: value.policyholderLastName,
      relationshipToPolicyholder: value.relationshipToPolicyholder,
      policyholderGender: value.policyholderGender,
      policyholderDOB: value.policyholderDOB,
    };

    // Patient DOB is collected on patient-info. Until then, skip full patient payload
    // validation/save to avoid surfacing unrelated backend validation errors in this step.
    if (!currentContextData.dob) {
      const formInfo: FormDetails = {
        isCompleted: true,
        submittedData: currentContextData,
      };
      setData("policyholderForm", formInfo);
      setIsLoading(false);
      return formInfo;
    }

    const patientDataPayload = mapContextToPatientData(currentContextData, utmTags);

    // Validate the payload
    const validation = validatePatientDataPayload(patientDataPayload, data.userUuid);
    if (!validation.isValid) {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "PolicyHolderDetailsForm.handleSubmit",
        `Patient data validation failed: ${validation.error}`,
      );

      setIsLoading(false);
      setErrors({
        ...errors,
        policyholderFirstName: validation.error || "Failed to validate patient data",
      });
      return { isCompleted: false };
    }

    // Save to backend
    if (data.userUuid) {
      const result = await PatientDataService.savePatientData(
        data.userUuid,
        patientDataPayload as any,
      );

      if (result.error) {
        ClientLogsService.logError(
          data.userUuid || "unknown",
          "PolicyHolderDetailsForm.handleSubmit",
          `Failed to save patient data to backend: ${result.error}`,
        );

        setIsLoading(false);
        setErrors({
          ...errors,
          policyholderFirstName: result.error,
        });
        return { isCompleted: false };
      }
    } else {
      setIsLoading(false);
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "PolicyHolderDetailsForm.handleSubmit",
        "User UUID not available. Skipping backend save.",
      );
    }

    const formInfo: FormDetails = {
      isCompleted: true,
      submittedData: currentContextData,
    };
    setData("policyholderForm", formInfo);
    setIsLoading(false);

    return formInfo;
  };

  const [value, setValue] = useState({
    policyholderFirstName: data.policyholderFirstName || "",
    policyholderLastName: data.policyholderLastName || "",
    relationshipToPolicyholder: data.relationshipToPolicyholder || "",
    policyholderGender: data.policyholderGender || "",
    policyholderDOB: data.policyholderDOB || "",
  });

  // ─── Draft-save on "I'm here" ─────────────────────────────────
  const saveToContext = useCallback(() => {
    setData("policyholderFirstName", value.policyholderFirstName || undefined);
    setData("policyholderLastName", value.policyholderLastName || undefined);
    setData("relationshipToPolicyholder", value.relationshipToPolicyholder || undefined);
    setData("policyholderGender", value.policyholderGender || undefined);
    setData("policyholderDOB", value.policyholderDOB || undefined);
  }, [value, setData]);

  useIdleFormFlush(saveToContext, { flushOnUnmount: true });

  const [errors, setErrors] = useState({
    policyholderFirstName: "",
    policyholderLastName: "",
    relationshipToPolicyholder: "",
    policyholderGender: "",
    policyholderDOB: "",
  });

  const validateField = (field, value) => {
    if (field === "policyholderFirstName") {
      return validateName(value, {
        requiredMessage: "Please enter policyholder’s first name",
        invalidMessage: "Please enter a valid policyholder’s first name",
      });
    }
    if (field === "policyholderLastName") {
      return validateName(value, {
        requiredMessage: "Please enter policyholder’s last name",
        invalidMessage: "Please enter a valid policyholder’s last name",
      });
    }
    if (field === "relationshipToPolicyholder" && !value) {
      return labels.errorRelationship;
    }
    if (field === "policyholderGender" && !value) {
      return "Please select gender as displayed on policy";
    }
    const policyholderDOBError = "Please enter a valid policyholder’s date of birth";
    if (field === "policyholderDOB" && !value) {
      return policyholderDOBError;
    }
    if (field === "policyholderDOB" && value) {
      if (!isValidDateOfBirth(value)) {
        return policyholderDOBError;
      } // Ensure policyholder is at least 18 years old
      const dob = validateDateOfBirth(value);
      if (dob && dob > getEighteenYearsAgeDate()) {
        return policyholderDOBError;
      }
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {
      policyholderFirstName: "",
      policyholderLastName: "",
      relationshipToPolicyholder: "",
      policyholderGender: "",
      policyholderDOB: "",
    };

    Object.keys(value).forEach((field) => {
      const error = validateField(field, value[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setErrors(newErrors);
    return Object.values(newErrors).every((v) => !v);
  };

  const handleChange = (field, fieldValue) => {
    const formattedValue = fieldValue;
    setValue({ ...value, [field]: formattedValue });
    // Clear error when user starts typing; full validation runs on submit
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleFormSubmit = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission behavior
  };

  useCanvasPrefillSync(setValue, {
    policyholderFirstName: (d) => d.policyholderFirstName,
    policyholderLastName: (d) => d.policyholderLastName,
    relationshipToPolicyholder: (d) => d.relationshipToPolicyholder,
    policyholderGender: (d) => d.policyholderGender,
    policyholderDOB: (d) => d.policyholderDOB,
  });

  useEffect(() => {
    setHandleSubmit(() => handleSubmit);
  }, [value]);

  return (
    <Fragment>
      {/* Loading Overlay for form validation and patient data saving */}
      <LoadingOverlay isLoading={isLoading} />
      <Card
        className={isMobile ? "v2card-form-mobile-style" : ""}
        css={{
          width: "100%",
          padding: "2rem",
        }}
      >
        <form id={patientInfoFormIds.POLICY_HOLDER_DETAILS} onSubmit={handleFormSubmit}>
          <FormSpacing>
            <TextInput
              data-testid="test-id-policyholder-first-name-input"
              label="Policyholder’s first name"
              value={value.policyholderFirstName}
              onChange={(e) => handleChange("policyholderFirstName", e.target.value)}
              isRequired
              errorMessage={errors.policyholderFirstName}
              aria-label="Enter Policyholder’s first name"
            />
            <TextInput
              data-testid="test-id-policyholder-last-name-input"
              label="Policyholder’s last name"
              value={value.policyholderLastName}
              onChange={(e) => handleChange("policyholderLastName", e.target.value)}
              isRequired
              errorMessage={errors.policyholderLastName}
              aria-label="Enter Policyholder’s last name"
            />
            <SelectInput
              data-testid="test-id-policyholder-relationship-select"
              enableOutsideScroll={canvasIsInSalesforce}
              label={labels.labelRelationship}
              value={value.relationshipToPolicyholder}
              onChange={(e) => handleChange("relationshipToPolicyholder", e)}
              isRequired
              errorMessage={errors.relationshipToPolicyholder}
              options={RelationshipOptions}
              aria-label={labels.ariaRelationship}
            />
            <SelectInput
              data-testid="test-id-policyholder-gender-select"
              enableOutsideScroll={canvasIsInSalesforce}
              label="Policyholder’s gender (as displayed on the insurance policy)"
              value={value.policyholderGender}
              onChange={(e) => handleChange("policyholderGender", e)}
              isRequired
              errorMessage={errors.policyholderGender}
              options={LegalSexOptions}
              aria-label="Select Policyholder’s gender"
            />
            <StyledDateInput
              data-testid="test-id-policyholder-dob-input"
              isRequired
              label="Policyholder’s date of birth (DOB)"
              maxDate={dayjs(getEighteenYearsAgeDate())}
              value={value.policyholderDOB}
              onChange={(e) => {
                const policyholderDobValue = typeof e === "string" ? e : e?.target?.value;
                handleChange("policyholderDOB", policyholderDobValue || "");
              }}
              errorMessage={errors.policyholderDOB}
              aria-label="Enter Policyholder’s date of birth"
            />
          </FormSpacing>
        </form>
      </Card>
    </Fragment>
  );
};
